import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  readSavedThemePreference,
  readSystemTheme,
  resolveThemePreference,
  subscribeToSavedThemePreference,
  subscribeToSystemTheme,
  writeSavedThemePreference,
  type ThemePreference,
  type ThemeType,
} from './theme-preference';

interface ThemeContextType {
  theme: ThemeType;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  /**
   * Apply a backend-provided default theme (e.g. company settings
   * `default_theme`). Only takes effect when the user has not already saved a
   * personal theme preference in localStorage, so an explicit choice always
   * wins. Safe to call repeatedly (no-op once a theme has been persisted).
   */
  applyDefaultTheme: (defaultTheme?: ThemeType | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [savedPreference, setSavedPreference] = useState<ThemePreference | null>(() =>
    readSavedThemePreference(),
  );
  const [defaultTheme, setDefaultTheme] = useState<ThemeType | null>(null);
  const [systemTheme, setSystemTheme] = useState<ThemeType>(() => readSystemTheme());

  useEffect(
    () => subscribeToSavedThemePreference(() => setSavedPreference(readSavedThemePreference())),
    [],
  );
  useEffect(() => subscribeToSystemTheme(() => setSystemTheme(readSystemTheme())), []);

  const themePreference = savedPreference ?? defaultTheme ?? 'system';
  const theme = resolveThemePreference(themePreference, systemTheme);

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setSavedPreference(preference);
    writeSavedThemePreference(preference);
  }, []);

  // Preserve the existing binary toggle for consumers that have not moved to
  // the three-option selector yet. Toggling always becomes an explicit choice.
  const toggleTheme = useCallback(() => {
    setThemePreference(theme === 'dark' ? 'light' : 'dark');
  }, [setThemePreference, theme]);

  const applyDefaultTheme = useCallback((defaultTheme?: ThemeType | null) => {
    if (defaultTheme !== 'light' && defaultTheme !== 'dark') {
      return;
    }
    // A saved (explicit) preference always wins over the backend default.
    if (readSavedThemePreference() !== null) {
      return;
    }
    setDefaultTheme(defaultTheme);
  }, []);

  // ensure this code is in ThemeProvider
  useEffect(() => {
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${theme}-theme`);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ theme, themePreference, setThemePreference, toggleTheme, applyDefaultTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// custom hook, convenient to use in components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export type { ThemePreference, ThemeType, ThemeContextType };
